import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flame, Users as CircleIcon, Sparkles as SparklesIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";

const LOGO = "/brand/logo.png";

export default function LogoPage() {
    return (
        <div data-testid="logo-page">
            <Seo
                title="Notre logo · Foundation of Light"
                description="La symbolique du logo Foundation of Light — le méditant, le cercle, le feu sacré. Une signature vivante née d'une transmission reçue en 2024."
                path="/logo"
            />
            {/* Hero */}
            <section className="relative overflow-hidden bg-[#0b1424]">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-b from-[#0b1424] via-[#0b1424]/95 to-[#0b1424]" />
                    <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-amber-300/10 blur-3xl" />
                    <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full bg-secondary/15 blur-3xl" />
                </div>
                <div className="relative max-w-4xl mx-auto px-6 py-32 text-center">
                    <div className="text-xs tracking-[0.4em] uppercase text-amber-200/80 mb-8 font-medium">Notre symbole</div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        transition={{ duration: 1.2, ease: "easeOut" }}
                        className="w-40 h-40 sm:w-52 sm:h-52 rounded-full bg-amber-100/95 ring-1 ring-amber-200/60 shadow-2xl mx-auto mb-10 flex items-center justify-center"
                    >
                        <img src={LOGO} alt="Logo Foundation of Light" className="w-[85%] h-[85%] object-contain" />
                    </motion.div>
                    <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-tight text-white mb-6">
                        Trois symboles,<br />
                        <span className="italic text-amber-200">un même mouvement de retour à la Lumière.</span>
                    </h1>
                    <p className="text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
                        Ce logo n'a pas été dessiné, il a été reçu — comme un rappel silencieux du chemin que la
                        Foundation of Light propose d'incarner. Voici la lecture des trois éléments qui le composent.
                    </p>
                </div>
            </section>

            {/* Le méditant */}
            <section className="max-w-4xl mx-auto px-6 py-24" data-testid="logo-meditant">
                <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}>
                    <div className="text-sm tracking-[0.3em] uppercase text-secondary mb-4 font-medium">I · Le méditant</div>
                    <h2 className="font-serif text-3xl sm:text-4xl mb-6">La paix intérieure, assise au cœur du monde.</h2>
                    <div className="space-y-5 text-base sm:text-lg text-foreground/80 leading-relaxed">
                        <p>
                            Au centre du logo repose la silhouette d'un être en méditation. Simple, dépouillée,
                            elle rappelle que la première transformation n'a besoin d'aucun outil, d'aucun décor,
                            d'aucun savoir — seulement d'un retour à soi.
                        </p>
                        <p>
                            La méditation, ici, n'est pas seulement une pratique. C'est l'état d'accueil profond
                            qui permet à la Lumière de descendre, à la conscience de s'ouvrir, aux tensions
                            de se dénouer. Elle est le fondement discret sur lequel tout le reste peut se déployer.
                        </p>
                        <p className="font-serif italic text-xl text-foreground/90 py-2">
                            Trouver la paix intérieure, c'est déjà participer à la paix du monde.
                        </p>
                    </div>
                </motion.div>
            </section>

            {/* Le cercle */}
            <section className="bg-accent/40 py-24" data-testid="logo-cercle">
                <div className="max-w-4xl mx-auto px-6">
                    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}>
                        <div className="text-sm tracking-[0.3em] uppercase text-secondary mb-4 font-medium">II · Le cercle</div>
                        <h2 className="font-serif text-3xl sm:text-4xl mb-6">L'unité vivante qui relie toutes choses.</h2>
                        <div className="space-y-5 text-base sm:text-lg text-foreground/80 leading-relaxed">
                            <p>
                                Autour du méditant s'inscrit le cercle — figure sans commencement ni fin,
                                symbole ancien de l'entièreté, du sacré et de la communauté rassemblée.
                            </p>
                            <p>
                                Il évoque le cercle de conscience dans lequel chacun est accueilli tel qu'il est.
                                Le cercle des êtres visibles et invisibles qui accompagnent notre évolution.
                                Le cercle de la Terre, de Gaïa, de la Vie qui pulse en tout ce qui est vivant.
                            </p>
                            <p>
                                Il rappelle aussi que nul n'avance seul. Que l'éveil individuel se tisse
                                à l'éveil collectif, et qu'aucune expérience — aussi intime soit-elle — n'est
                                dépourvue de résonance sur l'ensemble.
                            </p>
                            <p className="font-serif italic text-xl text-foreground/90 py-2">
                                Nous sommes des points de conscience dans un même cercle de Lumière.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Le feu sacré */}
            <section className="max-w-4xl mx-auto px-6 py-24" data-testid="logo-feu">
                <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}>
                    <div className="text-sm tracking-[0.3em] uppercase text-secondary mb-4 font-medium">III · Le feu sacré</div>
                    <h2 className="font-serif text-3xl sm:text-4xl mb-6">La Lumière divine qui traverse, éclaire et transforme.</h2>
                    <div className="space-y-5 text-base sm:text-lg text-foreground/80 leading-relaxed">
                        <p>
                            Enfin, s'élève au-dessus du méditant la flamme du feu sacré. Elle est la présence
                            de la Lumière divine, cette source vivante qui existait avant toute chose et qui
                            demeure en toute chose.
                        </p>
                        <p>
                            Le feu sacré n'est pas un feu qui brûle — c'est un feu qui révèle. Il éclaire les
                            zones que la conscience n'avait pas encore visitées. Il consume ce qui doit être
                            libéré, sans blesser. Il transmet des fréquences qui viennent réharmoniser
                            l'être en profondeur.
                        </p>
                        <p>
                            Il est aussi le rappel que chacun porte en lui une étincelle de cette même flamme.
                            La reconnaître, l'honorer, en prendre soin — c'est cela, entrer sur le chemin de la Lumière.
                        </p>
                        <p className="font-serif italic text-xl text-foreground/90 py-2">
                            La Lumière ne s'apprend pas. Elle se laisse traverser.
                        </p>
                    </div>
                </motion.div>
            </section>

            {/* Invitation */}
            <section className="bg-accent/40 py-24">
                <div className="max-w-3xl mx-auto px-6">
                    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }} className="rounded-3xl border border-secondary/40 bg-gradient-to-br from-secondary/10 via-transparent to-primary/5 p-10 sm:p-14 text-center">
                        <div className="flex justify-center gap-6 mb-8">
                            <div className="p-4 rounded-full bg-background/60 border border-border/40"><SparklesIcon className="w-6 h-6 text-secondary" /></div>
                            <div className="p-4 rounded-full bg-background/60 border border-border/40"><CircleIcon className="w-6 h-6 text-primary" /></div>
                            <div className="p-4 rounded-full bg-background/60 border border-border/40"><Flame className="w-6 h-6 text-amber-500" /></div>
                        </div>
                        <h2 className="font-serif text-3xl mb-6">Un logo, trois portes d'entrée.</h2>
                        <p className="text-base sm:text-lg text-foreground/80 leading-relaxed mb-8">
                            Selon le moment de votre chemin, l'un des trois éléments résonnera plus fort en vous.
                            Nul besoin de tout comprendre d'un coup — laissez le symbole faire son œuvre discrète,
                            et laissez venir à vous les expériences qui vous appellent.
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center">
                            <Link to="/evenements">
                                <Button className="rounded-full h-12 px-6 bg-primary text-primary-foreground hover:scale-[1.03] transition-transform" data-testid="logo-cta-events">
                                    Découvrir les ateliers <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                            <Link to="/reservation">
                                <Button variant="outline" className="rounded-full h-12 px-6" data-testid="logo-cta-booking">
                                    Réserver un soin
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
